/* The theme's one carousel implementation, shared by the hero slideshow
   and every product row.

   It is a scroll-snap track, not a transform-based slider: the browser
   does the panning, momentum and snapping natively, so touch behaviour is
   correct for free and the track stays usable if this script never runs.
   The script only adds the arrows, the dots and the autoplay.

   A root carrying data-carousel-fade (currently just the hero) opts out of
   that scroll-snap positioning — see slideshow.liquid's CSS — in favour of
   an absolutely-stacked crossfade, so this tracks the active slide with a
   plain index and an is-active class instead of reading scroll geometry.
   Everything else (arrows, dots, autoplay, hover/focus pausing) is the same
   code path either way, so a fade carousel is a drop-in for a scrolling one
   rather than a second implementation. */
(function () {
  // Each section that needs a carousel requests this file, so the browser
  // executes it once per <script> tag. Without this guard every root gets
  // re-bound on each pass — five click handlers, and five autoplay timers
  // fighting over the same hero.
  if (window.__pbCarouselReady) return;
  window.__pbCarouselReady = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setup(root) {
    if (root.dataset.carouselBound === 'true') return;

    var track = root.querySelector('[data-carousel-track]');
    if (!track) return;

    root.dataset.carouselBound = 'true';

    var slides = Array.prototype.slice.call(track.children);
    if (slides.length < 2) {
      root.classList.add('is-single');
      return;
    }

    var prev = root.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-carousel-dot]'));
    var autoplay = root.hasAttribute('data-carousel-autoplay');
    var interval = parseInt(root.getAttribute('data-carousel-interval'), 10) || 6000;
    var timer = null;

    var fadeMode = root.hasAttribute('data-carousel-fade');
    var fadeIndex = 0;

    function currentIndex() {
      if (fadeMode) return fadeIndex;

      // Nearest slide to the track's scroll position, which stays correct
      // whether the shopper used the arrows, a dot, or a swipe.
      var pos = track.scrollLeft;
      var best = 0;
      var bestDistance = Infinity;

      slides.forEach(function (slide, index) {
        var distance = Math.abs(slide.offsetLeft - track.offsetLeft - pos);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });

      return best;
    }

    function goTo(index) {
      var clamped = Math.max(0, Math.min(index, slides.length - 1));

      if (fadeMode) {
        fadeIndex = clamped;
        slides.forEach(function (slide, slideIndex) {
          slide.classList.toggle('is-active', slideIndex === clamped);
        });
        syncControls();
        return;
      }

      var target = slides[clamped];
      if (!target) return;
      track.scrollTo({
        left: target.offsetLeft - track.offsetLeft,
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    function syncControls() {
      var index = currentIndex();

      dots.forEach(function (dot, dotIndex) {
        var active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      // A non-looping track disables the arrow at each end rather than
      // leaving a control that silently does nothing.
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
    }

    if (prev) {
      prev.addEventListener('click', function () {
        goTo(currentIndex() - 1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        goTo(currentIndex() + 1);
      });
    }

    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        goTo(index);
      });
    });

    // A fade carousel never actually scrolls (see goTo above), so there's
    // no scroll position for this listener to react to.
    if (!fadeMode) {
      var scrollTimer = null;
      track.addEventListener(
        'scroll',
        function () {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(syncControls, 80);
        },
        { passive: true }
      );
    }

    // --- autoplay ------------------------------------------------------

    function advance() {
      if (document.visibilityState === 'hidden') return;
      var index = currentIndex();
      goTo(index >= slides.length - 1 ? 0 : index + 1);
    }

    function start() {
      if (timer || !autoplay || reduceMotion.matches) return;
      timer = window.setInterval(advance, interval);
    }

    function stop() {
      window.clearInterval(timer);
      timer = null;
    }

    if (autoplay) {
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') stop();
        else start();
      });
      start();
    }

    if (fadeMode) slides[0].classList.add('is-active');
    syncControls();
  }

  document.querySelectorAll('[data-carousel]').forEach(setup);

  // Tracks that arrive after load — recommendations, a filtered grid, a
  // section re-rendered by the theme editor — announce themselves here
  // rather than being polled for.
  document.addEventListener('pb:carousel-init', function (event) {
    var target = event.target;
    if (target && target.matches && target.matches('[data-carousel]')) setup(target);
  });

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-carousel]').forEach(setup);
  });
})();
